/**
 * Curated emoji set for the dashboard's emoji picker.
 *
 * We deliberately ship our own small list instead of pulling in
 * emoji-mart or emoji-picker-element — both would add ~60-200kb to the
 * bundle and neither is needed for a 16-person team's chat UI.
 *
 * Ordering within each category roughly follows "what a work team
 * actually uses in Slack": reactions first, people/faces second,
 * then hands, hearts, objects.
 */

export const EMOJI_CATEGORIES = [
  {
    id: 'reactions',
    label: 'Reactions',
    emojis: [
      '👍', '👎', '👌', '🙏', '🙌', '👏', '🤝', '💪',
      '🔥', '🎉', '🚀', '💯', '✅', '❌', '⚠️', '❓',
      '✨', '⭐', '🌟', '💡', '📌', '🎯', '🏆', '🥇',
    ],
  },
  {
    id: 'smileys',
    label: 'Smileys',
    emojis: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
      '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
      '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪',
      '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨',
      '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥',
      '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕',
      '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯',
      '🤠', '🥳', '😎', '🤓', '🧐', '😕', '😟', '🙁',
      '☹️', '😮', '😯', '😲', '😳', '🥺', '😦', '😧',
      '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣',
      '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠',
    ],
  },
  {
    id: 'gestures',
    label: 'Gestures',
    emojis: [
      '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤏', '✌️',
      '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕',
      '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜',
      '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅',
    ],
  },
  {
    id: 'hearts',
    label: 'Hearts',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
      '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖',
      '💘', '💝', '💟',
    ],
  },
  {
    id: 'objects',
    label: 'Objects',
    emojis: [
      '📊', '📈', '📉', '📅', '📆', '🗓️', '📋', '📌',
      '📎', '🖇️', '🗂️', '📁', '📂', '📄', '📝', '✏️',
      '🖊️', '🖌️', '🔍', '🔎', '💻', '🖥️', '⌨️', '🖱️',
      '📱', '☎️', '📞', '💬', '💭', '🗯️', '💡', '🔔',
      '🔕', '📣', '📢', '☕', '🍵', '🍺', '🍕', '🍔',
      '🎂', '🍰', '🍪', '🍩',
    ],
  },
]

/**
 * Flat list of every emoji we know about — used for simple search.
 */
export const ALL_EMOJIS = EMOJI_CATEGORIES.flatMap((c) => c.emojis)

/**
 * Lightweight name index so users can type "smile" or "fire" and find
 * a relevant emoji. Only the most-used names are indexed; the search
 * falls back to category match when nothing hits.
 */
export const EMOJI_NAMES = {
  '👍': ['thumbs up', 'yes', 'ok', 'approve', 'like'],
  '👎': ['thumbs down', 'no', 'dislike'],
  '🙏': ['pray', 'thanks', 'thank you', 'please'],
  '🙌': ['hands up', 'celebrate', 'praise'],
  '👏': ['clap', 'applause'],
  '🤝': ['handshake', 'deal', 'agree'],
  '💪': ['strong', 'flex', 'muscle'],
  '🔥': ['fire', 'lit', 'hot'],
  '🎉': ['party', 'celebrate', 'tada'],
  '🚀': ['rocket', 'launch', 'ship'],
  '💯': ['100', 'hundred', 'perfect'],
  '✅': ['check', 'done', 'yes', 'complete'],
  '❌': ['x', 'no', 'wrong', 'cancel'],
  '⚠️': ['warning', 'caution', 'alert'],
  '❓': ['question', 'huh'],
  '✨': ['sparkle', 'new', 'shiny'],
  '⭐': ['star'],
  '🌟': ['glowing star', 'excellent'],
  '💡': ['idea', 'lightbulb', 'bright'],
  '📌': ['pin', 'pinned'],
  '🎯': ['target', 'bullseye', 'goal'],
  '🏆': ['trophy', 'win', 'first'],
  '😀': ['smile', 'happy', 'grin'],
  '😂': ['laugh', 'joy', 'cry laughing'],
  '🤣': ['rofl', 'laugh hard'],
  '😊': ['smile', 'blush', 'happy'],
  '😍': ['heart eyes', 'love'],
  '🥰': ['smiling hearts', 'love'],
  '😎': ['cool', 'sunglasses'],
  '🤔': ['thinking', 'hmm'],
  '🙄': ['eye roll', 'really'],
  '😬': ['grimace', 'yikes', 'awkward'],
  '😭': ['crying', 'sob'],
  '😡': ['angry', 'mad'],
  '❤️': ['red heart', 'love'],
  '💔': ['broken heart'],
  '💬': ['speech', 'comment', 'chat'],
  '📊': ['chart', 'bar chart', 'data'],
  '📈': ['chart up', 'growth', 'trending up'],
  '📉': ['chart down', 'decline'],
  '📅': ['calendar', 'date'],
  '☕': ['coffee'],
  '🍕': ['pizza'],
}

/**
 * Search across both the name index and raw emoji (if someone pastes
 * an emoji into the search box). Returns up to `limit` matches.
 */
export function searchEmojis(query, limit = 48) {
  const q = query.trim().toLowerCase()
  if (!q) return null
  const hits = []
  for (const emoji of ALL_EMOJIS) {
    const names = EMOJI_NAMES[emoji] || []
    if (emoji.includes(q) || names.some((n) => n.includes(q))) {
      hits.push(emoji)
      if (hits.length >= limit) break
    }
  }
  return hits
}
